import {
  Plug,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Package,
  Clock,
  ShoppingBag,
  Users,
  MapPin,
  Boxes,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import { formatDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useConnectionCheck, useConnection, useClearSyncProgress } from '@/hooks/useSettings';
import { useBrowserSync } from '@/hooks/useBrowserSync';
import { UserManagementCard } from '@/components/UserManagementCard';
import { logException } from '@/lib/logger';
import type { BrowserSyncScope } from '@/lib/browser-sync';

export function SettingsPage() {
  // Live connection check — re-runs on every visit to this page.
  const connectionCheck = useConnectionCheck();
  // `connection` is only used for sync progress (resume / stuck detection).
  const { data: connection } = useConnection();
  const browserSync = useBrowserSync();
  const clearSync = useClearSyncProgress();

  const connectionValid = connectionCheck.data?.valid === true;
  const connectionProgress = connection?.sync_progress ?? null;
  const activeProgress = browserSync.progress ?? connectionProgress;
  const runningProgress = browserSync.isRunning ? browserSync.progress : connectionProgress;

  const handleSync = async (scope: BrowserSyncScope = 'full') => {
    try {
      const isFullSync = scope === 'full';
      toast.success(
        isFullSync && connection?.sync_progress?.phase
          ? 'Browser sync resumed'
          : `${scope === 'full' ? 'Full' : scope.replace('_', ' ')} sync started`,
      );
      const result = await browserSync.start(scope);

      if (result.status === 'success') {
        toast.success(`${scope === 'full' ? 'Full' : scope.replace('_', ' ')} sync complete`);
        return;
      }

      if (result.status === 'cancelled') {
        toast.success('Sync cancelled');
        return;
      }

      const lastError = result.errors[result.errors.length - 1] ?? 'Unknown sync error';
      toast.error(`Sync failed: ${lastError}`);
    } catch (err) {
      logException(err, { operation: 'browserSync', source: 'settings_page' });
      toast.error(`Sync failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="w-full max-w-2xl flex-1 space-y-6 overflow-auto p-4 md:p-6">
        {/* Connection Status — checked live on every visit */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Plug className="h-4 w-4" />
              Shopify Connection
            </CardTitle>
            <CardDescription>
              Checked live against Shopify each time you open this page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {connectionCheck.isFetching ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking Shopify connection…
              </div>
            ) : connectionValid ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="text-sm text-foreground font-medium">Connected</span>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider">
                    Store
                  </label>
                  <p className="text-sm text-foreground mt-0.5">
                    {connectionCheck.data?.shop_name ?? '—'}
                  </p>
                  {connectionCheck.data?.shop_domain && (
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">
                      {connectionCheck.data.shop_domain}
                    </p>
                  )}
                </div>
                {connectionCheck.data?.plan && (
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">
                      Plan
                    </label>
                    <p className="text-sm text-foreground mt-0.5">{connectionCheck.data.plan}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span className="text-sm text-destructive font-medium">Connection problem</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {connectionCheck.data?.error ??
                    connectionCheck.error?.message ??
                    'Could not verify the Shopify connection.'}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void connectionCheck.refetch()}
                  disabled={connectionCheck.isFetching}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Check again
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Manual Sync */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Manual Sync
            </CardTitle>
            <CardDescription>
              Pull products, orders, customers, and inventory from Shopify into the database.
              Webhooks keep data current automatically — use this to backfill or recover.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={() => { void handleSync(); }}
                  disabled={browserSync.isRunning || !connectionValid}
                  className="w-full sm:w-auto"
                >
                  {browserSync.isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      {connectionProgress?.phase ? 'Resume Sync' : 'Run Full Sync'}
                    </>
                  )}
                </Button>

                {browserSync.isRunning && (
                  <Button
                    variant="outline"
                    onClick={browserSync.cancel}
                    className="w-full sm:w-auto"
                  >
                    Cancel Sync
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start min-w-0"
                  onClick={() => { void handleSync('products'); }}
                  disabled={browserSync.isRunning || !connectionValid}
                >
                  <Package className="h-3.5 w-3.5 mr-1 shrink-0" />
                  <span className="truncate">Products</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start min-w-0"
                  onClick={() => { void handleSync('recent_products'); }}
                  disabled={browserSync.isRunning || !connectionValid}
                >
                  <Clock className="h-3.5 w-3.5 mr-1 shrink-0" />
                  <span className="truncate">Recent Products</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start min-w-0"
                  onClick={() => { void handleSync('inventory'); }}
                  disabled={browserSync.isRunning || !connectionValid}
                >
                  <Boxes className="h-3.5 w-3.5 mr-1 shrink-0" />
                  <span className="truncate">Inventory</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start min-w-0"
                  onClick={() => { void handleSync('customers'); }}
                  disabled={browserSync.isRunning || !connectionValid}
                >
                  <Users className="h-3.5 w-3.5 mr-1 shrink-0" />
                  <span className="truncate">Customers</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start min-w-0"
                  onClick={() => { void handleSync('locations'); }}
                  disabled={browserSync.isRunning || !connectionValid}
                >
                  <MapPin className="h-3.5 w-3.5 mr-1 shrink-0" />
                  <span className="truncate">Locations</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start min-w-0"
                  onClick={() => { void handleSync('orders'); }}
                  disabled={browserSync.isRunning || !connectionValid}
                >
                  <ShoppingBag className="h-3.5 w-3.5 mr-1 shrink-0" />
                  <span className="truncate">Orders</span>
                </Button>
              </div>

              {/* Stuck / stopped sync recovery */}
              {runningProgress?.phase && (() => {
                const staleMs = runningProgress.startedAt
                  ? Date.now() - new Date(runningProgress.startedAt).getTime()
                  : 0;
                const isStopped =
                  runningProgress.status === 'failed' || runningProgress.status === 'cancelled';
                const isStale = !browserSync.isRunning && (isStopped || staleMs > 3 * 60 * 1000);
                return isStale ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2 text-sm text-warning">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {isStopped
                        ? `Sync ${runningProgress.status} on "${runningProgress.phase}" phase`
                        : `Sync appears stuck on "${runningProgress.phase}" phase`}
                      <span className="text-muted-foreground">
                        (started {Math.round(staleMs / 60_000)}m ago)
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        clearSync.mutate(undefined, {
                          onSuccess: () => toast.success('Stuck sync cleared'),
                          onError: (err) => toast.error(`Failed: ${err.message}`),
                        });
                      }}
                      disabled={clearSync.isPending}
                    >
                      {clearSync.isPending ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Clearing...</>
                      ) : (
                        'Clear stuck sync'
                      )}
                    </Button>
                  </div>
                ) : null;
              })()}

              {activeProgress?.phase && (
                <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3 sm:p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {activeProgress.message ?? `Working on ${activeProgress.phase}`}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Phase: {activeProgress.phase}
                        {activeProgress.resource ? ` • Resource: ${activeProgress.resource}` : ''}
                        {activeProgress.currentPage ? ` • Page ${activeProgress.currentPage}` : ''}
                        {activeProgress.currentLocationId
                          ? ` • Location ${activeProgress.currentLocationId}`
                          : ''}
                      </p>
                    </div>
                    <div className="text-left text-xs text-muted-foreground sm:text-right">
                      {activeProgress.startedAt && (
                        <p>Started {formatDateTime(activeProgress.startedAt)}</p>
                      )}
                      {activeProgress.fetched != null && (
                        <p>
                          {activeProgress.fetched}
                          {activeProgress.total != null ? ` / ${activeProgress.total}` : ''} fetched
                        </p>
                      )}
                    </div>
                  </div>

                  {activeProgress.retrying && (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-warning">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Retrying {activeProgress.retrying.operation} in{' '}
                      {Math.round(activeProgress.retrying.waitMs / 1000)}s
                      {' '}({activeProgress.retrying.attempt}/{activeProgress.retrying.maxAttempts})
                    </div>
                  )}

                  {!!activeProgress.counts && Object.keys(activeProgress.counts).length > 0 && (
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:grid-cols-4">
                      {Object.entries(activeProgress.counts).map(([key, value]) => (
                        <div key={key} className="rounded border border-border bg-background px-2 py-1">
                          <span className="font-medium text-foreground">{value}</span> {key}
                        </div>
                      ))}
                    </div>
                  )}

                  {!!activeProgress.errors?.length && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-destructive">Recent errors</p>
                      {activeProgress.errors.slice(-3).map((error) => (
                        <p key={error} className="text-xs text-destructive/90">
                          {error}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* User Management */}
        <UserManagementCard />
      </div>
    </div>
  );
}
